import AddIcon from '@mui/icons-material/Add';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import {
  Grid2,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import Button from '@mui/material/Button';
import { User } from '@thro-types/users';

import { parseAsInteger, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAdminElections, useAdminElectionsCounts } from 'api/hooks/polyas/polyas';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { ElectionList } from './components/election-list';
import { ElectionListSkeleton } from './components/election-skeletons';
import { ElectionStatusFilter } from './components/election-status-filter';

interface Props {
  currentUser: User;
}

const PAGE_SIZE_OPTIONS = [6, 12, 24, 48];

export default function ElectionAdminPage({ currentUser }: Props) {
  const { t } = useTranslation();
  usePageHeading(t(translations.pages.polyas.header()));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(6));
  const [state, setState] = useQueryState('state');
  const navigate = useNavigate();

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when page size changes
    refetch();
  };

  const {
    data: elections,
    error,
    isLoading,
    refetch
  } = useAdminElections({ params: { page, limit, state } });
  const { data: electionsCounts } = useAdminElectionsCounts();

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  if (isLoading) {
    return <ElectionListSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t(translations.pages.polyas.detail.errorLoadingElection())}: {error.message}
      </Alert>
    );
  }

  return (
    <Grid2 size={12}>
      <Grid2
        size={12}
        container
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        paddingY={1}
      >
        <ElectionStatusFilter value={state ?? 'all'} onChange={setState} counts={electionsCounts} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/election/admin/edit')}
        >
          {t(translations.pages.polyas.edit.createButton())}
        </Button>
      </Grid2>

      {/* No elections Found */}
      {!elections?.data || elections?.data.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'inline-flex'
            }}
          >
            <HowToVoteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            {t(translations.pages.polyas.noElectionsFound())}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mx: 'auto' }}>
            {state === 'all'
              ? t(translations.pages.polyas.notEligibleForAnyElections())
              : t(translations.pages.polyas.noActiveElections())}
          </Typography>
        </Box>
      ) : (
        // Elections List
        <>
          <ElectionList elections={elections?.data} />
          {elections.meta.total > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {elections.meta.total} {t(translations.pages.polyas.election())}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="page-size-label">
                    {t(translations.pages.polyas.perPage())}
                  </InputLabel>
                  <Select
                    labelId="page-size-label"
                    value={limit}
                    label={t(translations.pages.polyas.perPage())}
                    onChange={e => handleLimitChange(Number(e.target.value))}
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Pagination
                count={elections.meta.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}
    </Grid2>
  );
}
